import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdPlaceholder from './AdPlaceholder';

const AdSlot = ({ 
  placementCode, 
  className = '', 
  fallbackContent = null,
  onImpression = null,
  onClick = null,
  placeholderVariant = 'hero',
  placeholderWidth = 1200,
  placeholderHeight = 300
}) => {
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [impressionTracked, setImpressionTracked] = useState(false);
  const adRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchAd();
  }, [placementCode]);

  useEffect(() => {
    if (ad && !impressionTracked && adRef.current) {
      // Use Intersection Observer to track impression when ad is visible
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && !impressionTracked) {
              trackImpression();
              setImpressionTracked(true);
              observer.disconnect();
            }
          });
        },
        { threshold: 0.5 } // Ad must be 50% visible
      );

      observer.observe(adRef.current);

      return () => observer.disconnect();
    }
  }, [ad, impressionTracked]);

  const fetchAd = async () => {
    try {
      setLoading(true);
      console.log('[AdSlot] Fetching ad for placement:', placementCode);
      const response = await axios.get(`${backendUrl}/api/ads/serve/${placementCode}`);
      console.log('[AdSlot] Response:', response.data);
      
      if (response.data.ad) {
        console.log('[AdSlot] Ad loaded successfully:', response.data.ad.creative_name);
        setAd(response.data.ad);
      } else {
        console.log('[AdSlot] No ad available:', response.data.message);
        setAd(null);
      }
    } catch (err) {
      console.error('[AdSlot] Error fetching ad:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const trackImpression = async () => {
    if (!ad) return;
    
    try {
      await axios.post(`${backendUrl}/api/ads/impression`, null, {
        params: {
          assignment_id: ad.assignment_id,
          placement_code: placementCode
        }
      });
      
      if (onImpression) {
        onImpression(ad);
      }
    } catch (err) {
      console.error('Error tracking impression:', err);
    }
  };

  const handleClick = async () => {
    if (!ad) return;
    
    try {
      await axios.post(`${backendUrl}/api/ads/click`, null, {
        params: {
          assignment_id: ad.assignment_id,
          placement_code: placementCode
        }
      });
      
      if (onClick) {
        onClick(ad);
      }
      
      // Open click URL in new tab
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Error tracking click:', err);
      // Still open the URL even if tracking fails
      window.open(ad.click_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (loading) {
    return fallbackContent || (
      <AdPlaceholder 
        width={placeholderWidth} 
        height={placeholderHeight} 
        variant={placeholderVariant} 
      />
    );
  }

  if (error || !ad) {
    return fallbackContent || (
      <AdPlaceholder 
        width={placeholderWidth} 
        height={placeholderHeight} 
        variant={placeholderVariant} 
      />
    );
  }

  return (
    <div 
      ref={adRef}
      className={`ad-slot cursor-pointer relative ${className}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => e.key === 'Enter' && handleClick()}
      style={{ minHeight: '200px' }}
    >
      {ad.file_type === 'video' || ad.file_type.startsWith('video/') ? (
        <video
          src={`${backendUrl}${ad.file_url}`}
          alt={ad.alt_text}
          className="w-full h-full object-cover"
          style={{ maxHeight: '400px' }}
          autoPlay
          muted
          loop
          playsInline
        />
      ) : (
        <img
          src={`${backendUrl}${ad.file_url}`}
          alt={ad.alt_text}
          className="w-full h-auto object-contain"
          style={{ maxHeight: '400px' }}
          loading="lazy"
        />
      )}
      
      {/* Sponsored label for transparency */}
      <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded z-10">
        Sponsored
      </div>
    </div>
  );
};

export default AdSlot;
